import { format } from 'date-fns';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

import Slider from '@components/common/Slider/Slider';
import DatePicker from '@components/form/DatePicker/DatePicker';

import useTranslation from '@hooks/useTranslation';

import { useAssetTreeQuery } from '@services/useAssetTreeQuery';
import { useCustomerInfoQuery } from '@services/useCustomerInfoQuery';
import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { selectAppLocationSelectValue, selectAppRtl } from '@store/useAppStore';

import { parseDateOrNull } from '../../helpers/parseDateOrNull';
import type { IArchivedAiFilterData, IArchivedAiStore } from '../../model/archivedAi';
import { selectArchiveFiltersData, useArchiveFiltersActions } from '../../store/useArchiveFiltersStore';
import style from './style.modules.scss';

interface IProps {
  id: string;
  visible: boolean;
  setIsOpen: (value: boolean) => void;
  filterData: IArchivedAiFilterData | undefined;
}

const AdditionalFilters: React.FC<IProps> = ({ id, visible, filterData, setIsOpen }) => {
  const [calendarOpen, setCalendarOpen] = useState(false);

  const { data: profileData } = useUserProfileQuery();
  const { customerId } = useParams();

  const { t } = useTranslation();
  const locationSelectValue = selectAppLocationSelectValue();

  const { data: customerInfoData } = useCustomerInfoQuery({
    customerId,
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
  });
  const { data: assetTreeData } = useAssetTreeQuery();

  const rtl = selectAppRtl();
  const {
    timeUpdatedRange: { minTimeUpdated: filterMinTimeUpdated, maxTimeUpdated: filterMaxTimeUpdated },
    timeCreatedReportRange: { minTimeCreated: filterMinTimeCreated, maxTimeCreated: filterMaxTimeCreated },
    costAvoidedRange: { minCostAvoided: filterMinCostAvoided, maxCostAvoided: filterMaxCostAvoided },
  }: IArchivedAiStore = selectArchiveFiltersData();

  const { setArchiveFiltersKey } = useArchiveFiltersActions();

  const isSelectedAllFacility = locationSelectValue.id === 'all';

  const currentCurrency = isSelectedAllFacility
    ? assetTreeData?.customerCurrency
    : locationSelectValue.currency;

  const minTimeUpdated = parseDateOrNull(filterData?.minTimeUpdated);
  const minTimeCreatedReport = parseDateOrNull(filterData?.minTimeCreatedReport);

  return (
    <Tooltip
      id={id}
      place="bottom"
      clickable
      className={style.additionalFilters}
      disableStyleInjection
      setIsOpen={(open) => {
        if (!open && calendarOpen) {
          return;
        }
        setIsOpen(open);
      }}
      openOnClick
      isOpen={visible}
      render={() => {
        return (
          <>
            <div className={style.tooltipTitle}>{t('Additional Filters')}</div>
            <div className={style.filters}>
              <div className={style.datepickerLastUpdateRange}>
                <DatePicker
                  label={t('Date Range')}
                  id="daterange-last-update-facilities"
                  placeholderText={t('Select Date Range')}
                  dateFormat={rtl ? 'yyyy/MM/dd' : `${profileData?.dateFormat.date}`}
                  startDate={filterMinTimeUpdated ? new Date(filterMinTimeUpdated) : undefined}
                  endDate={filterMaxTimeUpdated ? new Date(filterMaxTimeUpdated) : undefined}
                  minDate={minTimeUpdated as Date}
                  onChange={(values: [Date | null, Date | null]) =>
                    setArchiveFiltersKey('timeUpdatedRange', {
                      minTimeUpdated: values[0] ? format(values[0], 'MM/dd/yyyy') : undefined,
                      maxTimeUpdated: values[1] ? format(values[1], 'MM/dd/yyyy') : undefined,
                    })
                  }
                  selectsRange
                  showMonthDropdown
                  showYearDropdown
                  type="woHelperText"
                  onCalendarOpen={() => setCalendarOpen(true)}
                  onCalendarClose={() => setCalendarOpen(false)}
                />
              </div>
              <DatePicker
                label={t('Completion Report Creation Date')}
                id="daterange-completion-report-creation-facilities"
                placeholderText={t('Select Date Range')}
                dateFormat={rtl ? 'yyyy/MM/dd' : `${profileData?.dateFormat.date}`}
                startDate={filterMinTimeCreated ? new Date(filterMinTimeCreated) : undefined}
                endDate={filterMaxTimeCreated ? new Date(filterMaxTimeCreated) : undefined}
                minDate={minTimeCreatedReport as Date}
                onChange={(values: [Date | null, Date | null]) =>
                  setArchiveFiltersKey('timeCreatedReportRange', {
                    minTimeCreated: values[0] ? format(values[0], 'MM/dd/yyyy') : undefined,
                    maxTimeCreated: values[1] ? format(values[1], 'MM/dd/yyyy') : undefined,
                  })
                }
                selectsRange
                showMonthDropdown
                showYearDropdown
                type="woHelperText"
                onCalendarOpen={() => setCalendarOpen(true)}
                onCalendarClose={() => setCalendarOpen(false)}
              />
              {customerInfoData?.showTotalCostSavings && (
                <div className={style.averageTime}>
                  <Slider
                    label={t('Cost saving range')}
                    from={filterMinCostAvoided || filterData?.minCostAvoided || 0}
                    to={filterMaxCostAvoided || filterData?.maxCostAvoided || 0}
                    onChange={([left, right]) => {
                      setArchiveFiltersKey('costAvoidedRange', {
                        minCostAvoided: left,
                        maxCostAvoided: right,
                      });
                    }}
                    min={filterData?.minCostAvoided}
                    max={filterData?.maxCostAvoided}
                    startInput={<span className={style.startAdornment}>{currentCurrency.symbol}</span>}
                    showToCurrency
                  />
                </div>
              )}
            </div>
          </>
        );
      }}
    />
  );
};

export default AdditionalFilters;
